<template>
	<transition name="fade">
		<div
			v-if="loading"
			:class="[
				'fas-loading-indicator',
				{ 'fas-loading-indicator__full_page': !component },
				`fas-loading-indicator-color--${loaderBcgColor}`
			]"
		>
			<div
				:class="[
					'fas-loading-indicator__loading-container',
					{ 'has-text': message.length > 0 }
				]"
			>
				<div class="loading-animation" role="img" aria-label="Loading">
					<div
						:class="[
							'fas-loader',
							`fas-loader__type--${loaderType}`,
							`fas-loader__color--${colorStyle}`
						]"
					>
						<SpinnerSVG
							v-if="loaderType === 'spinner'"
							:loading="true"
							:spinnerStrokeWidth="spinnerStrokeWidth"
							:colorTheme="colorStyle"
						/>

						<div v-else-if="loaderType === 'legacy'">
							<div class="legacy-animation" role="image" aria-label="Loading">
								<span class="bar" />
								<span class="bar" />
								<span class="bar" />
								<span class="bar" />
								<span class="bar" />
								<span class="ball" />
							</div>
						</div>
					</div>
				</div>

				<div
					:class="[
						'fas-loading-indicator__loading-text',
						`fas-loading-indicator__loading-text__style--${loaderTextStyle}`,
						`fas-loading-indicator__loading-text__color--${loaderTextColor}`
					]"
					v-if="message.length > 0"
				>
					<p v-html="message" />
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
/**
 * The Loading Indicator description
 *
 * This component can appear within a relatively positioned component container or it can cover the full visible page.
 *
 * ### Usage
 *
 * For Angular and Vue.js usage code snippets, click the `Show code` button at the bottom right of every story example.
 *
 */

import SpinnerSVG from "../spinner-svg/SpinnerSVG.vue";

export default {
	name: "LoadingIndicator",

	components: {
		SpinnerSVG
	},

	props: {
		/**
		 * When `true`, the Loading Indicator animation appears.
		 */
		loading: {
			type: Boolean,
			default: true
		},

		/**
		 * Set to `true` if this should be contained within a component container. When `false`, it will take up the entire browser window.
		 */
		component: {
			type: Boolean,
			default: true
		},

		/**
		 * Set background color of the entire spinner. Default is `rgba(255, 255, 255, 0.9)` : white with 0.9 opacity. 'blackScrim' is `rgba(0, 0, 0, 0.9)` with 0.9 opacity.
		 *
		 * @values white, whiteScrim, transparent, blackScrim, black
		 */
		loaderBcgColor: {
			type: String,
			default: "whiteScrim",
			validator: (value) => {
				return [
					"whiteScrim",
					"white",
					"transparent",
					"black",
					"blackScrim"
				].includes(value);
			}
		},

		/**
		 * Fonts type and weight for the message.
		 *
		 * @values p1-reg, p1-bold, p2-reg, p2-bold, p3-reg, p3-bold
		 */
		loaderTextStyle: {
			type: String,
			default: "p1-reg",
			validator: (value) => {
				return [
					"p1-reg",
					"p1-bold",
					"p2-reg",
					"p2-bold",
					"p3-reg",
					"p3-bold"
				].includes(value);
			}
		},

		/**
		 * Set text color
		 *
		 * @values black, white
		 */
		loaderTextColor: {
			type: String,
			default: "black",
			validator: (value) => {
				return ["black", "white"].includes(value);
			}
		},

		/**
		 *  Message on the bottom(for mobile) or right(for tablet and desktop) side of the loading indicator. Accepts HTML or simple text.
		 */
		message: {
			type: String,
			default: ""
		},

		/**
		 * Loading indictator type
		 *
		 * @values spinner, legacy
		 */
		loaderType: {
			type: String,
			default: "spinner",
			validator: (value) => {
				return ["spinner", "legacy"].includes(value);
			}
		},

		/**
		 * Set color style of the spinner.
		 *
		 * @values red, blue, white
		 */
		colorStyle: {
			type: String,
			default: "red",
			validator: (value) => {
				return ["red", "blue", "white"].includes(value);
			}
		},

		/**
		 * Set the stroke width of the spinner
		 */
		spinnerStrokeWidth: {
			type: Number,
			default: 4
		}
	}
};
</script>

<style lang="scss" scoped>
.fas-loading-indicator {
	position: absolute;
	z-index: 100;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;

	display: flex;
	align-items: center;
	justify-content: center;

	&-color-- {
		&whiteScrim {
			background-color: rgba(255, 255, 255, 0.9);
		}

		&white {
			background-color: rgba(255, 255, 255, 1);
		}

		&transparent {
			background-color: rgba(255, 255, 255, 0);
		}

		&black {
			background-color: rgba(0, 0, 0, 1);
		}
		&blackScrim {
			background-color: rgba(0, 0, 0, 0.9);
		}
	}

	&__full_page {
		position: fixed;
	}

	&__loading-container {
		display: block;
		width: 100%;
		max-width: 14rem;

		@include from-md {
			display: flex;
			align-items: center;
			flex-direction: row;
		}

		&.has-text {
			@include from-md() {
				max-width: 24rem;
			}

			.loading-animation {
				.fas-loader__type--legacy {
					& > div {
						aspect-ratio: 1 / 0.6;
					}
				}

				.fas-loader .legacy-animation {
					@include from-md() {
						top: calc(50% - 24px);
					}
				}
			}
		}

		.loading-animation {
			display: block;
			width: 100%;
			max-width: 6rem;
			position: relative;
			margin: 0 auto;

			.fas-loader {
				display: block;
				width: 100%;

				& > div {
					display: flex;
					align-content: center;
					justify-content: center;
					width: 100%;
					aspect-ratio: 1 / 1;
				}

				&__color {
					&--red {
						.legacy-animation {
							.bar,
							.ball {
								background-color: $color--red-3;
							}
						}
					}

					&--blue {
						.legacy-animation {
							.bar,
							.ball {
								background-color: $color--blue-4;
							}
						}
					}

					&--white {
						.legacy-animation {
							.bar,
							.ball {
								background-color: $color--neutral-10;
							}
						}
					}
				}

				.legacy-animation {
					position: absolute;
					width: 75px;
					height: 100px;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);

					.bar {
						position: absolute;
						bottom: 0;
						width: 10px;
						height: 50%;
						// background: $color--red-3;
						transform-origin: center bottom;

						@for $i from 1 through 5 {
							&:nth-child(#{$i}) {
								left: ($i - 1) * 15px;
								transform: scale(1, $i * 0.2);
								animation: bar#{$i} 4s infinite;
							}
						}
					}

					.ball {
						position: absolute;
						bottom: 12px;
						left: 0;
						width: 10px;
						height: 10px;
						// background: $color--red-3;
						border-radius: 50%;
						animation: ball 4s infinite;
					}
				}
			}
		}
	}

	&__loading-text {
		padding-top: 2rem;
		width: 100%;

		@include from-md() {
			max-width: 18rem;
			padding: 0 0 0 2rem;
		}

		p {
			font-family: $font--default;
			text-align: center;
			@include from-md() {
				text-align: left;
			}
		}

		&__color-- {
			&black {
				color: $color--neutral-2;
			}

			&white {
				color: $color--neutral-10;
			}
		}

		&__style-- {
			&p1-reg,
			&p1-bold {
				p {
					font-size: 20px;
					line-height: 22px;
				}
			}

			&p1-bold {
				p {
					font-weight: $font-weight--bold;
				}
			}

			&p2-reg,
			&p2-bold {
				p {
					font-size: 16px;
					line-height: 18px;
				}
			}

			&p2-bold {
				p {
					font-weight: $font-weight--bold;
				}
			}

			&p3-reg,
			&p3-bold {
				p {
					font-size: 14px;
					line-height: 16px;
				}
			}

			&p3-bold {
				p {
					font-weight: $font-weight--bold;
				}
			}
		}
	}
}

@keyframes white-colors {
	0% {
		stroke: $color--neutral-10;
	}
	100% {
		stroke: $color--neutral-10;
	}
}

@keyframes ball {
	0% {
		transform: translate(0, 0);
	}
	5% {
		transform: translate(8px, -14px);
	}
	10% {
		transform: translate(15px, -10px);
	}
	17% {
		transform: translate(23px, -24px);
	}
	20% {
		transform: translate(30px, -20px);
	}
	27% {
		transform: translate(38px, -34px);
	}
	30% {
		transform: translate(45px, -30px);
	}
	37% {
		transform: translate(53px, -44px);
	}
	40% {
		transform: translate(60px, -40px);
	}
	50% {
		transform: translate(60px, 0);
	}
	57% {
		transform: translate(53px, -14px);
	}
	60% {
		transform: translate(45px, -10px);
	}
	67% {
		transform: translate(37px, -24px);
	}
	70% {
		transform: translate(30px, -20px);
	}
	77% {
		transform: translate(22px, -34px);
	}
	80% {
		transform: translate(15px, -30px);
	}
	87% {
		transform: translate(7px, -44px);
	}
	90% {
		transform: translate(0, -40px);
	}
	100% {
		transform: translate(0, 0);
	}
}

@keyframes bar1 {
	0% {
		transform: scale(1, 0.2);
	}
	40% {
		transform: scale(1, 0.2);
	}
	50% {
		transform: scale(1, 1);
	}
	90% {
		transform: scale(1, 1);
	}
	100% {
		transform: scale(1, 0.2);
	}
}

@keyframes bar2 {
	0% {
		transform: scale(1, 0.4);
	}
	40% {
		transform: scale(1, 0.4);
	}
	50% {
		transform: scale(1, 0.8);
	}
	90% {
		transform: scale(1, 0.8);
	}
	100% {
		transform: scale(1, 0.4);
	}
}

@keyframes bar3 {
	0% {
		transform: scale(1, 0.6);
	}
	100% {
		transform: scale(1, 0.6);
	}
}

@keyframes bar4 {
	0% {
		transform: scale(1, 0.8);
	}
	40% {
		transform: scale(1, 0.8);
	}
	50% {
		transform: scale(1, 0.4);
	}
	90% {
		transform: scale(1, 0.4);
	}
	100% {
		transform: scale(1, 0.8);
	}
}

@keyframes bar5 {
	0% {
		transform: scale(1, 1);
	}
	40% {
		transform: scale(1, 1);
	}
	50% {
		transform: scale(1, 0.2);
	}
	90% {
		transform: scale(1, 0.2);
	}
	100% {
		transform: scale(1, 1);
	}
}
</style>
